import { useEffect } from 'react';

import {
	type QueryClient,
	queryOptions,
	useMutation,
	useQueries,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import type { ReplaceOperation } from 'fast-json-patch/commonjs/core';

import { type GetResponse, gql, GraphQLClient, PublicApiClient } from '@tf/api';
import {
	createPublicSchemaId,
	objectRelationValue,
	profileRelationValue,
	saveFile,
	splitObjectId,
	T,
} from '@tf/utils';

import { getSchemaUrl } from '@/core/api/schemas';
import { buildCompoundFilter, type FilteringParams } from '@/core/api/utils/buildCompoundFilter';
import { getCaseIdAlias } from '@/core/api/utils/getCaseIdAlias';
import type { CompoundFilter, KeyValueFilter } from '@/core/api/utils/typeUtils';
import { useClusterId } from '@/core/appMeta';
import type { Case, CaseWithRequestMetadata } from '@/core/types/cases';
import type {
	FileInfo,
	GenericPayment,
	InwardDetails,
	PaymentComment,
	PaymentDecisionStatus,
	PaymentEvent,
	PaymentWithProfile,
	TransactionAssessment,
} from '@/core/types/payments';

export type SortingType = 'number' | 'string' | 'date' | 'boolean';

export type SortingParams = {
	sortByLabels?: string;
	sortByMeta?: string;
	sortByType?: SortingType;
	sortByDirection: 'ASC' | 'DESC';
};

export type PaginationParams = {
	offset: number;
	limit: number;
};

export const paymentSearchLabels = ['client_id', 'order_id'];
const metaFilterPaths = ['/@status', '/risk', '/@with/profile'];

const allPaymentsOptions = (
	pagination?: PaginationParams,
	sorting?: SortingParams,
	filtering?: FilteringParams,
	search?: string
) =>
	queryOptions({
		queryKey: ['payments', 'all', pagination, sorting, filtering, search],
		queryFn: async () => {
			const metaFiltering: FilteringParams = filtering
				? filtering.filter((f) => metaFilterPaths.includes(f.path))
				: [];
			const labelFiltering: FilteringParams = filtering
				? filtering.filter((f) => !metaFilterPaths.includes(f.path))
				: [];
			const metaCompoundFilter = buildCompoundFilter(metaFiltering, '', []);
			const labelCompoundFilter = buildCompoundFilter(labelFiltering, search, paymentSearchLabels);

			return GraphQLClient.getInstance().requestSafe<
				{ get: PaymentWithProfile[] },
				{
					offset: number;
					limit: number;
					sortByLabels?: string;
					sortByMeta?: string;
					sortByType?: SortingType;
					sortByDirection?: 'ASC' | 'DESC';
					labelsFilter?: CompoundFilter;
					metaFilter?: CompoundFilter;
				}
			>(
				gql`
					query AllPayments(
						$offset: Int!
						$limit: Int!
						$sortByLabels: JSONPointer
						$sortByMeta: JSONPointer
						$sortByType: SortType
						$sortByDirection: SortDirection
						$labelsFilter: KeyValueFilter
						$metaFilter: KeyValueFilter
					) {
						get(
							rootFilter: { archetype: "payment", labels: $labelsFilter, meta: $metaFilter }
							paginationFilter: {
								offset: $offset
								limit: $limit
								sortByLabels: $sortByLabels
								sortByMeta: $sortByMeta
								sortByType: $sortByType
								sortByDirection: $sortByDirection
							}
						) {
							id
							meta
							labels
							data
							nestedRelation(filter: { relationKind: "profile" }) {
								object {
									labels
								}
							}
						}
					}
				`,
				{
					offset: pagination?.offset ?? 0,
					limit: pagination?.limit ?? 10,
					sortByLabels: sorting?.sortByLabels,
					sortByMeta: sorting?.sortByMeta,
					sortByType: sorting?.sortByType,
					sortByDirection: sorting?.sortByDirection,
					labelsFilter: labelCompoundFilter,
					metaFilter: metaCompoundFilter,
				}
			);
		},
	});

export const useAllPayments = (
	pagination?: PaginationParams,
	sorting?: SortingParams,
	filtering?: FilteringParams,
	search?: string
) => {
	return useQuery(allPaymentsOptions(pagination, sorting, filtering, search));
};

const paymentOptions = (paymentObjectName: string) => {
	return queryOptions({
		queryKey: ['payment', paymentObjectName],
		queryFn: async () => {
			return GraphQLClient.getInstance().request<
				{ get: GenericPayment<any, any>[] },
				{ paymentObjectName: string }
			>(
				gql`
					query Payment($paymentObjectName: String!) {
						get(rootFilter: { uuid: $paymentObjectName }) {
							id
							meta
							labels
							data
							kind
							nestedRelation {
								object {
									id
									data
									labels
									kind
									archetype
								}
							}
						}
					}
				`,
				{ paymentObjectName }
			);
		},
		select: ({ get }) => (get?.[0] ?? null) as GenericPayment<any, any>,
	});
};

export const usePayment = (paymentObjectName: string) => {
	return useQuery(paymentOptions(paymentObjectName));
};

export const useSetPaymentAssigneesMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ paymentId, assignees }: { paymentId: string; assignees: string[] }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation SetPaymentAssignees($patch: [PatchInput!]!) {
						mutate(patch: $patch) {
							id
						}
					}
				`,
				{
					patch: [
						{
							objectId: paymentId,
							objectLabelsPatch: [{ op: 'add', path: '/assigned_managers', value: assignees }],
						},
					],
				}
			);
		},
		onSuccess: (_, { paymentId }) => {
			return Promise.all([
				invalidatePaymentQueryById(queryClient, paymentId),
				queryClient.invalidateQueries({ queryKey: ['payments', 'all'] }),
				queryClient.invalidateQueries({ queryKey: ['events'] }),
			]);
		},
	});
};

const paymentEventsOptions = (paymentObjectName: string, formId?: string) =>
	queryOptions<PaymentEvent[]>({
		queryKey: ['events', paymentObjectName, formId],
		queryFn: async () => {
			if (!formId) {
				return [];
			}

			const client = GraphQLClient.getInstance();

			const formEventsQuery = gql`
				query FormEvents($formId: JSON!) {
					formEvents: get(
						rootFilter: {
							archetype: "event"
							meta: {
								OR: [
									{ AND: [{ key: "/@with/inward_details", operator: EQ, value: $formId }] }
									{ AND: [{ key: "/@with/outward_details", operator: EQ, value: $formId }] }
								]
							}
						}
					) {
						id
						meta
						kind
						archetype
					}
				}
			`;

			const paymentEventsQuery = gql`
				query PaymentEvents($paymentObjectName: JSON!) {
					paymentEvents: get(
						rootFilter: {
							archetype: "event"
							meta: { AND: [{ key: "/@with/payment", operator: CONTAINS, value: $paymentObjectName }] }
						}
					) {
						id
						meta
						kind
						archetype
					}
				}
			`;

			const [{ formEvents }, { paymentEvents }] = await Promise.all([
				client.request<{ formEvents: PaymentEvent[] }, { formId: string }>(formEventsQuery, { formId }),
				client.request<{ paymentEvents: PaymentEvent[] }, { paymentObjectName: string }>(
					paymentEventsQuery,
					{ paymentObjectName }
				),
			]);

			return [...formEvents, ...paymentEvents];
		},
	});

export const usePaymentEvents = (paymentObjectName: string, formId?: string) => {
	return useQuery(paymentEventsOptions(paymentObjectName, formId));
};

const fileInfoOptions = (fileId: string) =>
	queryOptions({
		queryKey: ['file', fileId],
		queryFn: async () => {
			return GraphQLClient.getInstance().request<{ get: FileInfo[] }, { fileId: string }>(
				gql`
					query PaymentFile($fileId: ID!) {
						get(rootFilter: { archetype: "file", id: $fileId }) {
							id
							meta
						}
					}
				`,
				{ fileId }
			);
		},
		select: ({ get }) => get?.[0],
	});

export const useFilesInfo = (ids: string[]) => {
	return useQueries({ queries: ids.map((id) => fileInfoOptions(id)) });
};

const paymentCommentsOptions = (paymentObjectName: string) =>
	queryOptions({
		queryKey: ['comments', paymentObjectName],
		queryFn: async () => {
			return GraphQLClient.getInstance().request<
				{ get: PaymentComment[] },
				{ paymentObjectName: string }
			>(
				gql`
					query PaymentComments($paymentObjectName: JSON!) {
						get(
							rootFilter: {
								archetype: "record"
								kind: "payment_comment"
								meta: { AND: [{ key: "/@with/payment", operator: CONTAINS, value: $paymentObjectName }] }
							}
						) {
							id
							meta
							data
						}
					}
				`,
				{ paymentObjectName }
			);
		},
		select: ({ get }) => get,
	});

export const usePaymentComments = (paymentObjectName: string) => {
	return useQuery(paymentCommentsOptions(paymentObjectName));
};

export const useAddPaymentCommentMutation = () => {
	const commentSchema = getSchemaUrl('types/record/payment_comment');

	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async ({ paymentId, comment }: { paymentId: string; comment: string }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation AddPaymentComments($create: [ObjectInput!]!) {
						mutate(create: $create) {
							id
						}
					}
				`,
				{
					create: [
						{
							id: 'new://comment-1',
							schema: commentSchema,
							data: {
								payment: {
									_object: paymentId,
									_relationKind: 'payment',
								},
								comment,
							},
						},
					],
				}
			);
		},
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({ queryKey: ['comments'] }),
				queryClient.invalidateQueries({ queryKey: ['events'] }),
			]);
		},
	});
};

export const useCreateInwardDetailsMutation = () => {
	const formSchema = getSchemaUrl('finance/payment/inward_details');
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ form, paymentId }: { paymentId: string; form: InwardDetails }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation CreateInwardDetails($create: [ObjectInput]!, $patch: [PatchInput]!) {
						mutate(create: $create, patch: $patch) {
							id
						}
					}
				`,
				{
					patch: [
						{
							objectId: paymentId,
							objectDataPatch: [
								{
									op: 'add',
									path: '/forms/inward_details',
									value: {
										_object: 'new://inward_details',
										_relationKind: 'inward_details',
									},
								},
							],
						},
					],
					create: [
						{
							id: 'new://inward_details',
							schema: formSchema,
							data: form,
						},
					],
				}
			);
		},
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({ queryKey: ['payment'] }),
				queryClient.invalidateQueries({ queryKey: ['events'] }),
			]);
		},
	});
};

export const useUpdateInwardDetailsMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ diff, formId }: { formId: string; diff: ReplaceOperation<string>[] }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation UpdateInwardDetails($patch: [PatchInput]!) {
						mutate(patch: $patch) {
							id
						}
					}
				`,
				{
					patch: [
						{
							objectId: formId,
							objectDataPatch: diff,
						},
					],
				}
			);
		},
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({ queryKey: ['payment'] }),
				queryClient.invalidateQueries({ queryKey: ['events'] }),
			]);
		},
	});
};

export const useMakePaymentDecisionMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			paymentId,
			status,
			comment,
		}: {
			paymentId: string;
			status: PaymentDecisionStatus;
			comment: string;
		}) => {
			const statusChangedAt = new Date().toISOString();
			return GraphQLClient.getInstance().request(
				gql`
					mutation MakePaymentDecision($patch: [PatchInput]!) {
						mutate(patch: $patch) {
							id
						}
					}
				`,
				{
					patch: [
						{
							objectId: paymentId,
							objectMetaPatch: [
								{ op: 'replace', path: '/@status', value: status },
								{ op: 'add', path: '/@status_changed_at', value: statusChangedAt },
							],
							objectDataPatch: [
								{
									op: 'add',
									path: '/decision',
									value: {
										comment: comment,
										made_at: statusChangedAt,
									},
								},
							],
						},
					],
				}
			);
		},
		onSuccess: (_, { paymentId }) => {
			return Promise.all([
				invalidatePaymentQueryById(queryClient, paymentId),
				queryClient.invalidateQueries({ queryKey: ['events'] }),
			]);
		},
	});
};

export const useResendPaymentDecisionMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			paymentId,
			originalStatus,
		}: {
			paymentId: string;
			originalStatus: PaymentDecisionStatus;
		}) => {
			const statusChangedAt = new Date().toISOString();
			return GraphQLClient.getInstance().request(
				gql`
					mutation ResendPaymentDecision($patch: [PatchInput]!) {
						mutate(patch: $patch) {
							id
						}
					}
				`,
				{
					patch: [
						{
							objectId: paymentId,
							objectMetaPatch: [
								{ op: 'replace', path: '/@status', value: originalStatus },
								{ op: 'add', path: '/@status_changed_at', value: statusChangedAt },
							],
						},
					],
				}
			);
		},
		onSuccess: (_, { paymentId }) => {
			return Promise.all([
				invalidatePaymentQueryById(queryClient, paymentId),
				queryClient.invalidateQueries({ queryKey: ['events'] }),
			]);
		},
	});
};

export const useCreateDetailsAttachmentMutation = (direction: 'inward' | 'outward') => {
	const formSchema = getSchemaUrl(`finance/payment/${direction}_details`);
	const attachmentSchema = getSchemaUrl('types/general/file/form_attachment', true);
	const queryClient = useQueryClient();

	const detailsKey = `${direction}_details`;
	const newDetailsId = `new://${detailsKey}`;

	return useMutation({
		mutationFn: async ({ files, paymentId }: { files: File[]; paymentId: string }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation CreateAttachment(
						$files: [Upload!]!
						$patch: [PatchInput]!
						$formSchema: String!
						$attachmentSchema: String!
					) {
						mutate(
							patch: $patch
							create: [
								{
									id: "${newDetailsId}"
									schema: $formSchema
									data: {
										additional_documents: [
											{
												_object: "new://file"
												_relationKind: "payment_additional_attachment"
											}
										]
									}
								}
								{
									id: "new://file"
									schema: $attachmentSchema
									upload: { _multipart_id: "file1" }
									files: $files
								}
							]
						) {
							id
						}
					}
				`,
				{
					files,
					patch: [
						{
							objectId: paymentId,
							objectDataPatch: [
								{
									op: 'add',
									path: `/forms/${detailsKey}`,
									value: {
										_object: newDetailsId,
										_relationKind: detailsKey,
									},
								},
							],
						},
					],
					formSchema,
					attachmentSchema,
				}
			);
		},
		onSuccess: (_, { paymentId }) => {
			return Promise.all([
				invalidatePaymentQueryById(queryClient, paymentId),
				queryClient.invalidateQueries({ queryKey: ['events'] }),
			]);
		},
	});
};

export const useUpdateDetailsAttachmentMutation = () => {
	const queryClient = useQueryClient();
	const attachmentSchema = getSchemaUrl('types/general/file/form_attachment', true);

	return useMutation({
		mutationFn: async ({ files, formId }: { files: File[]; formId: string }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation UpdateAttachment($files: [Upload!]!, $formId: ID!, $attachmentSchema: String!) {
						mutate(
							patch: [
								{
									objectId: $formId
									objectDataPatch: [
										{
											op: "add"
											path: "/additional_documents/-"
											value: { _object: "new://file", _relationKind: "payment_additional_attachment" }
										}
									]
								}
							]
							create: [
								{
									id: "new://file"
									schema: $attachmentSchema
									upload: { _multipart_id: "file1" }
									files: $files
								}
							]
						) {
							id
						}
					}
				`,
				{
					files,
					formId,
					attachmentSchema,
				}
			);
		},
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({ queryKey: ['payment'] }),
				queryClient.invalidateQueries({ queryKey: ['events'] }),
			]);
		},
	});
};

export const useRemoveDetailsAttachmentMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ index, formId }: { index: number; formId: string }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation RemoveDetailsAttachment($patch: [PatchInput!]!) {
						mutate(patch: $patch) {
							id
						}
					}
				`,
				{
					patch: [
						{
							objectId: formId,
							objectDataPatch: [
								{
									op: 'remove',
									path: `/additional_documents/${index}`,
								},
							],
						},
					],
				}
			);
		},
		onSuccess: () => {
			return Promise.all([
				queryClient.invalidateQueries({ queryKey: ['payment'] }),
				queryClient.invalidateQueries({ queryKey: ['events'] }),
			]);
		},
	});
};

async function requestAndSave(path: string, fallbackName: string): Promise<void> {
	const api = PublicApiClient.getInstance();
	const response = await api.request({ path, method: 'GET' });

	if (!response.ok) {
		throw new Error(`Failed to download file: HTTP ${response.status}}`);
	}

	const contentDisposition = response.headers.get('Content-Disposition');
	const headerFileName = contentDisposition?.match(/filename="(.+)"/)?.[1];
	const fileName = headerFileName || fallbackName || 'download';

	const buf = await response.arrayBuffer();
	saveFile(buf, fileName);
}

export async function downloadAttachedFile(objectId: string, fileName = 'attached_file') {
	try {
		const encodedId = encodeURIComponent(objectId);
		await requestAndSave(`/v1/data/general_graph/object/${encodedId}/download`, fileName);
	} catch (err) {
		console.error('Error downloading attached file:', err);
		throw err;
	}
}

export async function getAttachedFileBlob(objectId: string): Promise<Blob> {
	const encodedId = encodeURIComponent(objectId);
	const response = await PublicApiClient.getInstance().request({
		path: `/v1/data/general_graph/object/${encodedId}/download`,
	});
	return response.blob();
}

export async function downloadEvidence(objectId: string, fileName = 'evidence.zip') {
	try {
		const base64PaymentId = btoa(objectId); // fine if objectId is ASCII-safe
		await requestAndSave(`/v1/data/download-evidence/${base64PaymentId}`, fileName);
	} catch (err) {
		console.error('Error downloading evidence:', err);
		throw err;
	}
}

const transactionAssessmentResultsOptions = (paymentObjectName: string) =>
	queryOptions({
		queryKey: ['assessment-results', paymentObjectName],
		queryFn: async () => {
			return GraphQLClient.getInstance().request<
				{ get: TransactionAssessment[] },
				{ paymentObjectName: string }
			>(
				gql`
					query TransactionAssessmentResults($paymentObjectName: JSON!) {
						get(
							rootFilter: {
								archetype: "record"
								kind: "transaction_assessment_result"
								meta: { AND: [{ key: "/@with/payment", operator: CONTAINS, value: $paymentObjectName }] }
							}
						) {
							id
							meta
							data
						}
					}
				`,
				{ paymentObjectName }
			);
		},
		select: ({ get }) => get,
	});

export const useTransactionAssessmentResults = (paymentObjectName: string) => {
	return useQuery(transactionAssessmentResultsOptions(paymentObjectName));
};

export const useAddPaymentAssessmentJobMutation = () => {
	const queryClient = useQueryClient();
	const paymentAssessmentJobSchema = getSchemaUrl(
		'types/finance/job/payment_transaction_assessment',
		true
	);

	return useMutation<{ mutate: { archetype: string; id: string }[] }, Error, { paymentId: string }>({
		mutationFn: async ({ paymentId }) => {
			return GraphQLClient.getInstance().request(
				gql`
					mutation CreatePaymentAssessmentJob($paymentId: ID!, $paymentAssessmentJobSchema: String!) {
						mutate(
							create: [
								{
									id: "new://payment_assessment_job"
									schema: $paymentAssessmentJobSchema
									data: { payload: { payment: { _object: $paymentId, _relationKind: "payment" } } }
								}
							]
						) {
							id
							schema
							archetype
							kind
							labels
							data
						}
					}
				`,
				{ paymentId, paymentAssessmentJobSchema }
			);
		},
	});
};

const traJobStatusOptions = (paymentObjectName: string) => {
	return queryOptions({
		queryKey: ['tra-job-status', paymentObjectName],
		queryFn: async () => {
			return GraphQLClient.getInstance().request<
				{ get: { meta: { '@status': string; '@updatedAt': string } }[] },
				{ paymentObjectName: string }
			>(
				gql`
					query GetJobStatus($paymentObjectName: JSON!) {
						get(
							rootFilter: {
								kind: "payment_transaction_assessment"
								meta: { AND: [{ key: "/@with/payment", operator: CONTAINS, value: $paymentObjectName }] }
							}
							paginationFilter: { sortByMeta: "/@createdAt", sortByDirection: DESC, limit: 1 }
						) {
							meta
							kind
						}
					}
				`,
				{ paymentObjectName }
			);
		},
		select: ({ get }) => get?.[0] ?? null,
		refetchInterval: (query) => {
			const jobStatus = query.state.data?.get[0].meta?.['@status'];
			if (jobStatus === 'PENDING' || jobStatus === 'IN_PROGRESS') {
				return 2500;
			}
			return false;
		},
	});
};

export const useTRAJobStatus = (paymentId: string) => {
	return useQuery(traJobStatusOptions(paymentId));
};

export const useCreatePaymentCaseMutation = () => {
	const queryClient = useQueryClient();
	const clusterId = useClusterId();

	return useMutation({
		mutationFn: async ({
			paymentId,
			profileId,
			recipientEmail,
		}: {
			paymentId: string;
			profileId: string;
			recipientEmail: string;
		}): Promise<Case[]> => {
			const statusChangedAt = new Date().toISOString();
			const res = await GraphQLClient.getInstance().request(
				gql`
					mutation CreatePaymentCase($objectsToCreate: [ObjectInput], $objectsToPatch: [PatchInput]) {
						mutate(create: $objectsToCreate, patch: $objectsToPatch) {
							id
							aliases
							archetype
							kind
							meta(path: ["/@author", "/@createdAt"])
							labels
						}
					}
				`,
				{
					objectsToCreate: [
						{
							id: 'new://case_1',
							schema: createPublicSchemaId({
								clusterId,
								schemaTag: `types/general/case/payment_information_request`,
							}),
							aliases: {
								[getCaseIdAlias()]: '@displayName',
							},
							labels: {
								recipient_email: recipientEmail,
								status: 'ACTIVE',
							},
							data: {
								profile: profileRelationValue(profileId),
								payment: objectRelationValue('payment', paymentId),
							},
						},
						{
							id: 'new://case_request_1',
							schema: createPublicSchemaId({
								clusterId,
								schemaTag: 'types/general/case/case_request',
							}),
							labels: {
								status: 'DRAFT',
							},
							data: {
								profile: profileRelationValue(profileId),
								payment: objectRelationValue('payment', paymentId),
								case: objectRelationValue('case', 'new://case_1'),
								request_text: '',
							},
						},
					],
					objectsToPatch: [
						{
							objectId: paymentId,
							objectMetaPatch: [
								{ op: 'replace', path: '/@status', value: 'PENDING_DETAILS' },
								{ op: 'add', path: '/@status_changed_at', value: statusChangedAt },
							],
						},
					],
				}
			);

			return res.mutate.filter((obj: { archetype: string }) => obj.archetype === 'case');
		},
		onSuccess: async (_, { paymentId }) => {
			await queryClient.invalidateQueries(paymentCasesQueryOptions(paymentId));
			return invalidatePaymentQueryById(queryClient, paymentId);
		},
	});
};

const paymentCasesQueryOptions = (paymentObjectName: string) => {
	return queryOptions({
		queryKey: ['paymentCases', paymentObjectName],
		queryFn: async (): Promise<CaseWithRequestMetadata[]> => {
			const casesData = await GraphQLClient.getInstance().request<
				GetResponse<Case>,
				{ paymentObjectName: string }
			>(
				gql`
					query PaymentCases($paymentObjectName: JSON!) {
						get(
							rootFilter: {
								archetype: "case"
								meta: { AND: [{ key: "/@with/payment", operator: CONTAINS, value: $paymentObjectName }] }
							}
							paginationFilter: {
								limit: 1000
								offset: 0
								sortByMeta: "/@createdAt"
								sortByDirection: DESC
							}
						) {
							id
							aliases
							kind
							meta(path: ["/@author", "/@createdAt"])
							labels
						}
					}
				`,
				{ paymentObjectName }
			);

			if (casesData.get.length === 0) {
				return [];
			}

			const caseLinkFilters = casesData.get.map(
				(c): KeyValueFilter => ({
					key: '/@with/case',
					operator: 'EQ',
					value: c.id,
				})
			);

			const caseRequests = await GraphQLClient.getInstance().request<
				GetResponse<CaseWithRequestMetadata['request']>,
				{ caseLinkFilters: KeyValueFilter[] }
			>(
				gql`
					query CaseRequestsForCases($caseLinkFilters: [KeyValueFilter!]!) {
						get(rootFilter: { kind: "case_request", meta: { OR: $caseLinkFilters } }) {
							id
							meta(path: ["/@with/case"])
							labels(path: ["/status", "/sent_at"])
							data
						}
					}
				`,
				{ caseLinkFilters }
			);

			return casesData.get.map((c) => {
				const request = caseRequests.get.find((r) => r.meta.case.includes(c.id));
				T.assertNotNullish(request, `no request found for case ${c.id}`);

				return {
					case: c,
					request,
				};
			});
		},
	});
};

export const usePaymentCases = (paymentObjectName: string) => {
	return useQuery(paymentCasesQueryOptions(paymentObjectName));
};

const invalidatePaymentQueryById = (queryClient: QueryClient, paymentId: string) => {
	const { objectName } = splitObjectId(paymentId);
	return queryClient.invalidateQueries(paymentOptions(objectName));
};

export const useInvalidateQueryOnJobCompletion = (isJobCompleted: boolean, queryKeys: string[]) => {
	const queryClient = useQueryClient();
	useEffect(() => {
		if (isJobCompleted) {
			for (const key of queryKeys) {
				queryClient.invalidateQueries({ queryKey: [key] });
			}
		}
	}, [isJobCompleted]);
};
